import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { Aviso, Botao, Cartao, Chip } from "../ui";
import { pedirJson } from "../../nucleo/api";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  ROTULO_DA_CATEGORIA,
  ROTULO_DO_STATUS,
  custoLegivel,
  dataLegivel,
  estadoDoChipDoStatus,
  useAtivo,
  useDefinirPessoasDoAtivo,
} from "../../nucleo/ativos";
import { ROTULO_DO_ESTADO } from "../../nucleo/tickets";

const esquemaCandidatas = z.array(z.object({ id: z.string().uuid(), nome: z.string(), email: z.string() }));

function useCandidatas(ativoId: string, habilitado: boolean) {
  return useQuery({
    queryKey: ["ativos", "candidatas", ativoId],
    enabled: habilitado,
    queryFn: ({ signal }) =>
      pedirJson(`/ativos/${encodeURIComponent(ativoId)}/pessoas/candidatas`, esquemaCandidatas, { signal }),
  });
}

/** O ativo por inteiro: dados, quem o usa, chamados ligados a ele e (para a equipe) o que é interno. */
export function DetalheDoAtivo({
  id,
  interno,
  podeGerenciar,
  aoEditar,
  caminhoDoChamado,
}: {
  id: string;
  interno: boolean;
  podeGerenciar: boolean;
  aoEditar?: () => void;
  caminhoDoChamado: (ticketId: string) => string;
}) {
  const ativo = useAtivo(id);
  const candidatas = useCandidatas(id, podeGerenciar);
  const definir = useDefinirPessoasDoAtivo();
  /* `null` = ainda não mexeu: vale o que o servidor tem. Derivar assim, em vez de copiar para o
     estado num efeito, é o que faz a lista acompanhar o ativo quando ele é recarregado. */
  const [alteradas, setAlteradas] = useState<string[] | null>(null);
  const doServidor = (ativo.data?.pessoas ?? []).map((pessoa) => pessoa.id);
  const escolhidas = alteradas ?? doServidor;

  if (ativo.isPending) return <p role="status">Carregando o ativo…</p>;
  if (ativo.isError) return <Aviso tom="erro">{mensagemDoConsole(ativo.error)}</Aviso>;
  const dados = ativo.data;

  function alternar(pessoaId: string, marcada: boolean) {
    const base = alteradas ?? doServidor;
    setAlteradas(marcada ? [...base, pessoaId] : base.filter((item) => item !== pessoaId));
  }

  return (
    <Cartao
      titulo={dados.nome}
      data-testid="detalhe-do-ativo"
      acessorio={<Chip estado={estadoDoChipDoStatus(dados.status)}>{ROTULO_DO_STATUS[dados.status]}</Chip>}
    >
      <dl className="grid gap-2 md:grid-cols-3 mb-3">
        <Item rotulo="Categoria" valor={ROTULO_DA_CATEGORIA[dados.categoria]} />
        <Item rotulo="Fabricante" valor={dados.fabricante} />
        <Item rotulo="Modelo" valor={dados.modelo} />
        <Item rotulo="Número de série" valor={dados.numero_serie} />
        <Item rotulo="Compra" valor={dataLegivel(dados.data_compra)} />
        <Item
          rotulo="Garantia"
          valor={
            dados.garantia_ate
              ? `${dataLegivel(dados.garantia_ate)}${dados.garantia_vencida ? " (vencida)" : ""}`
              : null
          }
        />
        <Item rotulo="Localização" valor={dados.localizacao} />
        {interno ? <Item rotulo="Organização" valor={dados.organizacao_nome ?? null} /> : null}
      </dl>
      {dados.descricao ? <p className="mb-3">{dados.descricao}</p> : null}
      {interno ? (
        <Aviso tom="neutro" className="mb-3" data-testid="dados-internos-do-ativo">
          <b>Só a equipe vê:</b> custo {custoLegivel(dados.custo)}
          {dados.observacao_interna ? ` · ${dados.observacao_interna}` : ""}
        </Aviso>
      ) : null}

      <h3 className="titulo-secao">Quem usa</h3>
      {!podeGerenciar ? (
        dados.pessoas.length === 0 ? (
          <p className="mb-3">Ninguém vinculado.</p>
        ) : (
          <ul className="mb-3">
            {dados.pessoas.map((pessoa) => (
              <li key={pessoa.id}>{pessoa.nome}</li>
            ))}
          </ul>
        )
      ) : (
        <div className="mb-3" data-testid="pessoas-do-ativo">
          {candidatas.isPending ? <p role="status">Carregando pessoas…</p> : null}
          {candidatas.data?.map((pessoa) => (
            <label key={pessoa.id} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={escolhidas.includes(pessoa.id)}
                onChange={(evento) => alternar(pessoa.id, evento.target.checked)}
              />
              <span>
                {pessoa.nome} <span className="celula-secundaria">{pessoa.email}</span>
              </span>
            </label>
          ))}
          {definir.isError ? <Aviso tom="erro" className="mt-2">{mensagemDoConsole(definir.error)}</Aviso> : null}
          <Botao
            className="mt-2"
            tamanho="pequeno"
            disabled={definir.isPending}
            onClick={() =>
              definir.mutate({ id, usuarioIds: escolhidas }, { onSuccess: () => setAlteradas(null) })
            }
            data-testid="salvar-pessoas-do-ativo"
          >
            {definir.isPending ? "Salvando…" : "Salvar quem usa"}
          </Botao>
        </div>
      )}

      <h3 className="titulo-secao">Chamados</h3>
      {dados.chamados.length === 0 ? (
        <p className="mb-3">Nenhum chamado ligado a este ativo.</p>
      ) : (
        <ul className="mb-3" data-testid="chamados-do-ativo">
          {dados.chamados.map((chamado) => (
            <li key={chamado.id}>
              <Link to={caminhoDoChamado(chamado.id)}>
                #{chamado.numero} · {chamado.assunto}
              </Link>{" "}
              <span className="celula-secundaria">
                {(ROTULO_DO_ESTADO as Record<string, string>)[chamado.estado] ?? chamado.estado}
              </span>
            </li>
          ))}
        </ul>
      )}
      {podeGerenciar && aoEditar ? (
        <Botao variante="fantasma" onClick={aoEditar} data-testid="editar-ativo">
          Editar ativo
        </Botao>
      ) : null}
    </Cartao>
  );
}

function Item({ rotulo, valor }: { rotulo: string; valor: string | null }) {
  return (
    <div>
      <dt className="celula-secundaria">{rotulo}</dt>
      <dd>{valor || "—"}</dd>
    </div>
  );
}
